// =====================================================================
//  ADMIN CONSOLE, UI PRIMITIVES
// ---------------------------------------------------------------------
//  The employee-facing pages each hand-rolled their own header, stat
//  card, table and modal, which is exactly why they all ended up looking
//  the same as one another and none of them looked deliberate. Every
//  admin screen composes from this file instead, so a change to the
//  console's look happens in one place.
//
//  Nothing here is HRMS-specific: no API calls, no domain vocabulary.
//  Domain widgets live with their pages.
// =====================================================================

import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Info,
  Inbox,
  Search,
  X,
} from 'lucide-react';
import { statusTone, roleTone } from './theme';
import { initials as toInitials, titleCase } from './format';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* =====================================================================
   HOOKS
   ================================================================== */

/** Debounces a rapidly-changing value (search boxes). */
export function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Calls `handler` on any click outside the referenced element. */
export function useOutsideClick(ref, handler, active = true) {
  useEffect(() => {
    if (!active) return undefined;
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) handler(event);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ref, handler, active]);
}

/** Escape-to-close, shared by the drawer, modal and command palette. */
export function useEscape(handler, active = true) {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') handler(event);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handler, active]);
}

/**
 * Locks background scrolling while an overlay is open, and restores the
 * exact previous value (not just `''`) so nested overlays don't fight.
 */
export function useScrollLock(active) {
  useLayoutEffect(() => {
    if (!active) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}

/** Persists a small piece of UI state (collapsed rail, page size). */
export function usePersistentState(key, initial) {
  const storageKey = `hrms.admin.${key}`;
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw === null ? initial : JSON.parse(raw);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* storage unavailable (private mode), state still works in memory */
    }
  }, [storageKey, value]);
  return [value, setValue];
}

/* =====================================================================
   TEXT & IDENTITY
   ================================================================== */

export function Avatar({ name, email, size = 'md', dark = false, className = '' }) {
  const sizeClass = size === 'sm' ? 'ac-avatar--sm' : size === 'lg' ? 'ac-avatar--lg' : '';
  return (
    <span
      className={cx('ac-avatar', sizeClass, dark && 'ac-avatar--dark', className)}
      aria-hidden="true"
      title={name || email || undefined}
    >
      {toInitials(name, email)}
    </span>
  );
}

/** Name over a muted secondary line, the standard identity cell. */
export function PersonCell({ name, sub, email, size = 'sm' }) {
  return (
    <div className="ac-cell-person">
      <Avatar name={name} email={email} size={size} />
      <div className="ac-cell-stack">
        <span className="ac-cell-main">{name || email || ','}</span>
        {sub ? <span className="ac-cell-sub">{sub}</span> : null}
      </div>
    </div>
  );
}

export function Stack({ main, sub }) {
  return (
    <div className="ac-cell-stack">
      <span className="ac-cell-main">{main}</span>
      {sub ? <span className="ac-cell-sub">{sub}</span> : null}
    </div>
  );
}

export function Eyebrow({ children, className = '' }) {
  return <div className={cx('ac-eyebrow', className)}>{children}</div>;
}

/* =====================================================================
   CHIPS
   ================================================================== */

export function Chip({ tone = 'neutral', dot = false, children, className = '', title, style }) {
  return (
    <span className={cx('ac-chip', tone !== 'neutral' && `ac-chip--${tone}`, className)} title={title} style={style}>
      {dot ? <span className="ac-chip-dot" /> : null}
      {children}
    </span>
  );
}

/** Chip whose colour is derived from a free-text status value. */
export function StatusChip({ value, dot = true, fallback = ',', className = '' }) {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: 'var(--ac-faint)' }}>{fallback}</span>;
  }
  return (
    <Chip tone={statusTone(value)} dot={dot} className={className}>
      {titleCase(value)}
    </Chip>
  );
}

export function RoleChip({ role }) {
  if (!role) return <span style={{ color: 'var(--ac-faint)' }}>,</span>;
  return <Chip tone={roleTone(role)}>{role === 'hr' ? 'HR' : titleCase(role)}</Chip>;
}

/* =====================================================================
   BUTTONS
   ================================================================== */

export function Button({
  variant = 'default',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  loading = false,
  block = false,
  children,
  className = '',
  type = 'button',
  disabled,
  ...rest
}) {
  const iconOnly = !children;
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cx(
        'ac-btn',
        variant !== 'default' && `ac-btn--${variant}`,
        size === 'sm' && 'ac-btn--sm',
        iconOnly && 'ac-btn--icon',
        block && 'ac-btn--block',
        className
      )}
      {...rest}
    >
      {loading ? (
        <span className="ac-spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />
      ) : (
        Icon && <Icon size={size === 'sm' ? 13 : 14} strokeWidth={2} />
      )}
      {children}
      {IconRight && !loading ? <IconRight size={size === 'sm' ? 13 : 14} strokeWidth={2} /> : null}
    </button>
  );
}

export function IconButton({ icon: Icon, label, size = 16, className = '', ...rest }) {
  return (
    <button type="button" className={cx('ac-iconbtn', className)} aria-label={label} title={label} {...rest}>
      <Icon size={size} strokeWidth={1.9} />
    </button>
  );
}

/* =====================================================================
   FORM CONTROLS
   ================================================================== */

export function Field({ label, required, hint, error, children, className = '', style }) {
  return (
    <label className={cx('ac-field', className)} style={style}>
      {label ? (
        <span className="ac-label">
          {label}
          {required ? <span className="ac-req"> *</span> : null}
        </span>
      ) : null}
      {children}
      {error ? <span className="ac-error">{error}</span> : hint ? <span className="ac-hint">{hint}</span> : null}
    </label>
  );
}

export function Input({ invalid, className = '', ...rest }) {
  return <input className={cx('ac-input', invalid && 'ac-input--invalid', className)} {...rest} />;
}

export function Textarea({ invalid, className = '', ...rest }) {
  return <textarea className={cx('ac-textarea', invalid && 'ac-input--invalid', className)} {...rest} />;
}

/**
 * Select. `options` accepts strings or `{ value, label }`. A `placeholder`
 * renders a disabled empty option so the control can start unset.
 */
export function Select({ options = [], placeholder, invalid, className = '', ...rest }) {
  return (
    <select className={cx('ac-select', invalid && 'ac-input--invalid', className)} {...rest}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => {
        const value = typeof option === 'string' ? option : option.value;
        const label = typeof option === 'string' ? option : option.label ?? option.value;
        return (
          <option key={String(value)} value={value}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search…', width = 240, className = '' }) {
  return (
    <div className={cx('ac-search', className)} style={{ width }}>
      <span className="ac-search-icon">
        <Search size={13} strokeWidth={2} />
      </span>
      <input
        className="ac-input"
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        style={{ paddingRight: value ? 26 : undefined }}
      />
      {value ? (
        <button type="button" className="ac-search-clear" onClick={() => onChange('')} aria-label="Clear search">
          <X size={12} strokeWidth={2.2} />
        </button>
      ) : null}
    </div>
  );
}

export function Checkbox({ checked, indeterminate = false, onChange, label, className = '', ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const input = (
    <input
      ref={ref}
      type="checkbox"
      className={cx('ac-checkbox', className)}
      checked={!!checked}
      onChange={onChange}
      {...rest}
    />
  );

  if (!label) return input;
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12.5 }}>
      {input}
      <span>{label}</span>
    </label>
  );
}

/* =====================================================================
   LAYOUT
   ================================================================== */

export function Panel({ children, className = '', pad = false, flush = false, style }) {
  return (
    <section
      className={cx('ac-panel', pad && 'ac-panel--pad', flush && 'ac-panel--flush', className)}
      style={style}
    >
      {children}
    </section>
  );
}

export function PanelHead({ title, sub, actions, children }) {
  return (
    <header className="ac-panel-head">
      <div style={{ minWidth: 0 }}>
        {title ? <h3 className="ac-panel-title">{title}</h3> : null}
        {sub ? <p className="ac-panel-sub">{sub}</p> : null}
        {children}
      </div>
      {actions ? <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: '0 0 auto' }}>{actions}</div> : null}
    </header>
  );
}

export function PanelBody({ children, flush = false, className = '', style }) {
  return (
    <div className={cx('ac-panel-body', flush && 'ac-panel-body--flush', className)} style={style}>
      {children}
    </div>
  );
}

export function PageHeader({ title, sub, actions, children }) {
  return (
    <header className="ac-pagehead">
      <div style={{ minWidth: 0 }}>
        <h1 className="ac-pagehead-title">{title}</h1>
        {sub ? <p className="ac-pagehead-sub">{sub}</p> : null}
        {children}
      </div>
      {actions ? <div className="ac-pagehead-actions">{actions}</div> : null}
    </header>
  );
}

export function Grid({ cols = 2, children, className = '', style }) {
  return (
    <div className={cx('ac-grid', `ac-grid--${cols}`, className)} style={style}>
      {children}
    </div>
  );
}

/* =====================================================================
   KPI TILE
   ================================================================== */

/**
 * Inline sparkline. Deliberately unlabelled and axis-free: it conveys
 * shape next to a precise number, and the number carries the value.
 */
export function Sparkline({ data = [], width = 68, height = 22, color = 'var(--ac-accent)', strokeWidth = 1.5 }) {
  const values = (data || []).map((d) => (typeof d === 'number' ? d : Number(d?.value) || 0));
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const y = (v) => height - 2 - ((v - min) / span) * (height - 4);

  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${y(v).toFixed(2)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  const gradientId = `ac-spark-${String(color).replace(/[^a-z0-9]/gi, '')}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Period-over-period change. `invert` is for metrics where down is good
 * (absences, open tickets) so the colour still means "better / worse"
 * rather than merely "up / down".
 */
export function Delta({ value, invert = false, suffix = '%' }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  const rounded = Math.round(Math.abs(n) * 10) / 10;
  const direction = n > 0 ? 'up' : n < 0 ? 'down' : 'flat';
  const good = invert ? n < 0 : n > 0;
  const tone = direction === 'flat' ? 'flat' : good ? 'up' : 'down';
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : ',';

  return (
    <span className={cx('ac-delta', `ac-delta--${tone}`)}>
      <span style={{ fontSize: 8 }}>{arrow}</span>
      {rounded}
      {suffix}
    </span>
  );
}

export function KpiTile({
  label,
  value,
  unit,
  icon: Icon,
  delta,
  deltaInvert = false,
  foot,
  spark,
  sparkColor,
  onClick,
  tone,
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className="ac-kpi" onClick={onClick} type={onClick ? 'button' : undefined}>
      <div className="ac-kpi-top">
        <span className="ac-kpi-label">{label}</span>
        {Icon ? (
          <span
            className="ac-kpi-icon"
            style={
              tone
                ? { background: `var(--ac-${tone}-bg)`, borderColor: `var(--ac-${tone}-bd)`, color: `var(--ac-${tone}-fg)` }
                : undefined
            }
          >
            <Icon size={13} strokeWidth={2} />
          </span>
        ) : null}
      </div>

      <div className="ac-kpi-row">
        <span className="ac-kpi-value">{value}</span>
        {unit ? <span className="ac-kpi-unit">{unit}</span> : null}
        <Delta value={delta} invert={deltaInvert} />
      </div>

      {spark && spark.length > 1 ? (
        <Sparkline data={spark} color={sparkColor} width={92} height={24} />
      ) : foot ? (
        <span className="ac-kpi-foot">{foot}</span>
      ) : null}
    </Tag>
  );
}

/* =====================================================================
   TABS & SEGMENTED CONTROL
   ================================================================== */

/** `items`: [{ key, label, count? }] */
export function Tabs({ items = [], value, onChange, className = '' }) {
  return (
    <div className={cx('ac-tabs', className)} role="tablist">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={value === item.key}
          className={cx('ac-tab', value === item.key && 'ac-tab--active')}
          onClick={() => onChange(item.key)}
        >
          {item.icon ? <item.icon size={14} strokeWidth={1.9} /> : null}
          {item.label}
          {item.count !== undefined && item.count !== null ? (
            <span className="ac-tab-count">{item.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function SegmentedControl({ items = [], value, onChange, className = '' }) {
  return (
    <div className={cx('ac-seg', className)} role="group">
      {items.map((item) => {
        const key = typeof item === 'string' ? item : item.key;
        const label = typeof item === 'string' ? item : item.label;
        return (
          <button
            key={String(key)}
            type="button"
            aria-pressed={value === key}
            className={cx('ac-seg-item', value === key && 'ac-seg-item--active')}
            onClick={() => onChange(key)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* =====================================================================
   OVERLAYS
   ================================================================== */

/**
 * Right-hand inspector panel. Records open here rather than on their own
 * page so the administrator keeps their place in the list behind it.
 */
export function Drawer({ open, onClose, title, sub, badge, tabs, activeTab, onTabChange, footer, wide = false, children }) {
  useEscape(onClose, open);
  useScrollLock(open);
  if (!open) return null;

  return (
    <>
      <div className="ac-overlay" onClick={onClose} aria-hidden="true" />
      <aside
        className={cx('ac-drawer', wide && 'ac-drawer--wide')}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Details'}
      >
        <div className="ac-drawer-head">
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 className="ac-drawer-title">{title}</h2>
              {badge}
            </div>
            {sub ? <p className="ac-drawer-sub">{sub}</p> : null}
          </div>
          <IconButton icon={X} label="Close" onClick={onClose} size={17} />
        </div>

        {tabs?.length ? (
          <div className="ac-drawer-tabs">
            <Tabs items={tabs} value={activeTab} onChange={onTabChange} className="" />
          </div>
        ) : null}

        <div className="ac-drawer-body">{children}</div>
        {footer ? <div className="ac-drawer-foot">{footer}</div> : null}
      </aside>
    </>
  );
}

export function Modal({ open, onClose, title, sub, footer, size = 'md', children }) {
  useEscape(onClose, open);
  useScrollLock(open);
  if (!open) return null;

  const sizeClass = size === 'lg' ? 'ac-modal--lg' : size === 'sm' ? 'ac-modal--sm' : '';

  return (
    <>
      <div className="ac-overlay" onClick={onClose} aria-hidden="true" />
      <div className="ac-modal-scroll" onClick={onClose}>
        <div
          className={cx('ac-modal', sizeClass)}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : 'Dialog'}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="ac-modal-head">
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <h2 className="ac-modal-title">{title}</h2>
              {sub ? <p className="ac-modal-sub">{sub}</p> : null}
            </div>
            <IconButton icon={X} label="Close" onClick={onClose} size={17} />
          </div>
          <div className="ac-modal-body">{children}</div>
          {footer ? <div className="ac-modal-foot">{footer}</div> : null}
        </div>
      </div>
    </>
  );
}

/**
 * Confirmation for destructive or irreversible actions. Deleting a person,
 * running payroll and approving a removal all route through this so the
 * console never mutates org-wide data on a single unguarded click.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  loading = false,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
        <span
          className="ac-empty-icon"
          style={{
            width: 30,
            height: 30,
            flex: '0 0 30px',
            background: tone === 'danger' ? 'var(--ac-neg-bg)' : 'var(--ac-warn-bg)',
            borderColor: tone === 'danger' ? 'var(--ac-neg-bd)' : 'var(--ac-warn-bd)',
            color: tone === 'danger' ? 'var(--ac-neg-fg)' : 'var(--ac-warn-fg)',
          }}
        >
          <AlertTriangle size={15} strokeWidth={2} />
        </span>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ac-body)', lineHeight: 1.55 }}>{message}</p>
      </div>
    </Modal>
  );
}

/* =====================================================================
   MENU
   ================================================================== */

/**
 * Dropdown menu. `trigger` receives `{ open, toggle }` so the caller keeps
 * control of what the button looks like.
 */
export function Menu({ trigger, children, align = 'right', placement = 'below', width }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(ref, close, open);
  useEscape(close, open);

  return (
    <div ref={ref} style={{ position: 'relative', flex: '0 0 auto' }}>
      {trigger({ open, toggle: () => setOpen((prev) => !prev) })}
      {open ? (
        <div
          className={cx('ac-menu', align === 'right' && 'ac-menu--right', `ac-menu--${placement}`)}
          style={width ? { minWidth: width } : undefined}
          onClick={close}
          role="menu"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function MenuItem({ icon: Icon, danger = false, children, ...rest }) {
  return (
    <button type="button" role="menuitem" className={cx('ac-menu-item', danger && 'ac-menu-item--danger')} {...rest}>
      {Icon ? <Icon size={14} strokeWidth={1.9} /> : null}
      {children}
    </button>
  );
}

export const MenuSeparator = () => <div className="ac-menu-sep" />;
export const MenuLabel = ({ children }) => <div className="ac-menu-label">{children}</div>;

/* =====================================================================
   DATA DISPLAY
   ================================================================== */

/** `items`: [{ key, value }], the standard record-detail layout. */
export function KeyValue({ items = [], className = '' }) {
  return (
    <dl className={cx('ac-kv', className)} style={{ margin: 0 }}>
      {items
        .filter((item) => item && item.key)
        .map((item) => (
          <div className="ac-kv-row" key={item.key}>
            <dt className="ac-kv-key">{item.key}</dt>
            <dd className="ac-kv-val" style={{ margin: 0 }}>
              {item.value === null || item.value === undefined || item.value === '' ? (
                <span style={{ color: 'var(--ac-faint)' }}>,</span>
              ) : (
                item.value
              )}
            </dd>
          </div>
        ))}
    </dl>
  );
}

export function Meter({ value = 0, max = 100, color = 'var(--ac-accent)' }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="ac-meter">
      <div className="ac-meter-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/** `segments`: [{ value, color, label }], a one-line composition bar. */
export function StackBar({ segments = [] }) {
  const total = segments.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
  if (total <= 0) return <div className="ac-stack" />;
  return (
    <div className="ac-stack">
      {segments.map((segment, index) => {
        const value = Number(segment.value) || 0;
        if (value <= 0) return null;
        return (
          <div
            key={segment.label || index}
            className="ac-stack-seg"
            style={{ width: `${(value / total) * 100}%`, background: segment.color }}
            title={`${segment.label || ''}: ${value}`}
          />
        );
      })}
    </div>
  );
}

export function Notice({ tone = 'info', icon: Icon, title, children, actions, className = '', style }) {
  const Fallback = tone === 'warning' || tone === 'negative' ? AlertTriangle : Info;
  const Resolved = Icon || Fallback;
  return (
    <div className={cx('ac-notice', tone !== 'default' && `ac-notice--${tone}`, className)} style={style}>
      <Resolved size={15} strokeWidth={2} style={{ flex: '0 0 15px', marginTop: 1 }} />
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        {title ? <div style={{ fontWeight: 650, marginBottom: children ? 3 : 0 }}>{title}</div> : null}
        {children}
      </div>
      {actions ? <div style={{ flex: '0 0 auto', display: 'flex', gap: 6 }}>{actions}</div> : null}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title = 'Nothing here yet', text, action }) {
  return (
    <div className="ac-empty">
      <span className="ac-empty-icon">
        <Icon size={17} strokeWidth={1.8} />
      </span>
      <div className="ac-empty-title">{title}</div>
      {text ? <p className="ac-empty-text" style={{ margin: 0 }}>{text}</p> : null}
      {action}
    </div>
  );
}

export function Spinner({ large = false, className = '' }) {
  return <span className={cx('ac-spinner', large && 'ac-spinner--lg', className)} role="status" aria-label="Loading" />;
}

export function LoadingBlock({ label = 'Loading…', height = 180 }) {
  return (
    <div
      style={{
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: 'var(--ac-faint)',
        fontSize: 12,
      }}
    >
      <Spinner large />
      {label}
    </div>
  );
}

export function Skeleton({ width = '100%', height = 12, radius, className = '', style }) {
  return (
    <span
      className={cx('ac-skeleton', className)}
      style={{ display: 'block', width, height, borderRadius: radius, ...style }}
    />
  );
}

/** Placeholder rows that match the grid's geometry while data loads. */
export function SkeletonRows({ rows = 6, cols = 5 }) {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: cols }).map((__, colIndex) => (
            <td key={colIndex}>
              <Skeleton width={colIndex === 0 ? '62%' : '44%'} height={10} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/* =====================================================================
   CHART TOOLTIP
   ================================================================== */

/** Recharts-compatible tooltip styled to the console. */
export function ChartTooltip({ active, payload, label, formatter, labelFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="ac-tooltip">
      {label !== undefined && label !== null ? (
        <div className="ac-tooltip-label">{labelFormatter ? labelFormatter(label) : label}</div>
      ) : null}
      {payload.map((entry) => (
        <div className="ac-tooltip-row" key={entry.dataKey ?? entry.name}>
          <span className="ac-tooltip-key">
            <span className="ac-tooltip-swatch" style={{ background: entry.color || entry.fill }} />
            {entry.name}
          </span>
          <span className="ac-tooltip-val">
            {formatter ? formatter(entry.value, entry.name, entry) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* =====================================================================
   TOOLBAR
   ================================================================== */

export function Toolbar({ children, className = '' }) {
  return <div className={cx('ac-toolbar', className)}>{children}</div>;
}

export const ToolbarSpacer = () => <div className="ac-toolbar-spacer" />;

/**
 * Dropdown filter rendered as a dashed chip that becomes solid once set, * so an administrator can tell at a glance whether they're looking at all
 * the data or a subset. That distinction matters when the number on screen
 * is going to be reported to someone.
 */
export function FilterChip({ label, value, options = [], onChange, allLabel = 'All' }) {
  const id = useId();
  const active = value !== '' && value !== undefined && value !== null;
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <label htmlFor={id} className={cx('ac-filterchip', active && 'ac-filterchip--on')} style={{ cursor: 'pointer' }}>
        <span style={{ opacity: 0.75 }}>{label}</span>
        <strong style={{ fontWeight: 700 }}>{active ? titleCase(value) : allLabel}</strong>
        <ChevronDown size={12} strokeWidth={2.2} />
      </label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? titleCase(option) : option.label;
          return (
            <option key={String(optionValue)} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </div>
  );
}

/* =====================================================================
   PAGINATION
   ================================================================== */

export function Pager({ page, pageSize, total, onPageChange, onPageSizeChange, sizes = [25, 50, 100, 200] }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="ac-pager">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="ac-pager-range">
          {total === 0 ? 'No records' : `${from},${to} of ${total.toLocaleString('en-IN')}`}
        </span>
        {onPageSizeChange ? (
          <select
            className="ac-select"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            style={{ height: 24, fontSize: 11, width: 'auto', paddingLeft: 7, paddingRight: 22 }}
            aria-label="Rows per page"
          >
            {sizes.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div className="ac-pager-controls">
        <Button
          size="sm"
          variant="ghost"
          icon={ChevronLeft}
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        />
        <span className="ac-pager-range" style={{ padding: '0 4px' }}>
          {page + 1} / {pageCount}
        </span>
        <Button
          size="sm"
          variant="ghost"
          icon={ChevronRight}
          disabled={page >= pageCount - 1}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        />
      </div>
    </div>
  );
}

/* =====================================================================
   TOAST BRIDGE
   ================================================================== */

// The app already mounts sonner's <Toaster/> in HRMSApp, so the console
// reuses it rather than introducing a second toast system.
export { toast } from 'sonner';

export { cx, Check };
